import { first } from 'lodash';

import {
  isCombinePipeline,
  isPipelineChildOf,
  isPipelineParentOf,
  isPipelineWithParent,
} from '@/core/pipeline';
import { getCellByPipelineId } from '@/core/cell/cells';
import { CellWithPipeline, isCellWithPipeline, isCellWithTitle } from '@/core/cell';
import { extractCombineSteps } from '@/core/operation/combine';
import { getPipelineByIdOrThrow } from '@/core/exploration';

import {
  Exploration,
  DereferencedPipeline,
  DereferencedPipelineOperation,
  Pipeline,
  PipelineOperation,
  FunnelOperation,
  DereferencedFunnelOperation,
  Fields,
  BasePipeline,
  Field,
  Model,
} from '../types';
import { getFlattenedStepPipeline, isEmptyStep } from '../edit-funnel/utils';

export { generatePipelineId } from '@/core/pipeline';
export * from './format';

export interface FieldGroup {
  name?: string;
  key: string;
  fields: Fields;
}

/**
 * Namespace constants for field groups in table/model joins.
 * Used in penguino expressions to reference fields from base and related models.
 */
export const FieldGroupNamespace = {
  BaseModel: 'base_model',
  RelatedModel: 'related_model',
} as const;

export type FieldOrGroup = Field | FieldGroup;

export type FieldsOrGroups = FieldOrGroup[];

export const isField = (fieldOrGroup: FieldOrGroup): fieldOrGroup is Field =>
  !isFieldGroup(fieldOrGroup) && 'type' in fieldOrGroup;

export const isFieldGroup = (fieldOrGroup: FieldOrGroup): fieldOrGroup is FieldGroup =>
  'fields' in fieldOrGroup;

export const flattenFieldGroups = (fields: FieldsOrGroups): Fields =>
  fields.flatMap((field) => (isFieldGroup(field) ? field.fields : field));

export const findField = (fields: FieldsOrGroups, key: string): Field | undefined =>
  fields.flatMap((f) => (isFieldGroup(f) ? f.fields : f)).find((f) => f.key === key);

export const findFieldFromGroups = (
  fields: FieldsOrGroups,
  lookup: { key: string; relation?: { key: string; baseModelId?: string } },
): Field | undefined => {
  if (lookup.relation !== undefined) {
    return flattenFieldGroups(fields).find(
      (field) =>
        field.key === lookup.key &&
        field.relation?.key === lookup.relation?.key &&
        (lookup.relation?.baseModelId === undefined ||
          field.relation?.baseModelId === lookup.relation?.baseModelId),
    );
  }

  return fields.filter(isField).find((field) => field.key === lookup.key);
};

export const filterFieldsBy = (
  fields: FieldsOrGroups,
  predicate: (field: Field) => boolean,
): FieldsOrGroups => {
  const result: FieldsOrGroups = [];

  fields.forEach((fieldOrGroup) => {
    if (isField(fieldOrGroup)) {
      if (predicate(fieldOrGroup)) {
        result.push(fieldOrGroup);
      }
    } else if (isFieldGroup(fieldOrGroup)) {
      const filteredFields = filterFieldsBy(fieldOrGroup.fields, predicate);
      if (filteredFields.length > 0) {
        result.push({
          ...fieldOrGroup,
          fields: filteredFields as Fields,
        });
      }
    }
  });

  return result;
};

export const firstField = (fields: FieldsOrGroups): Field | undefined =>
  first(fields.flatMap((f) => (isFieldGroup(f) ? f.fields : f)));

export const firstFieldBy = (
  fields: FieldsOrGroups,
  predicate: (field: Field) => boolean,
): Field | undefined =>
  first(fields.flatMap((f) => (isFieldGroup(f) ? f.fields : f)).filter(predicate));

export const getBaseModelId = (pipeline: Pipeline, exploration: Exploration) => {
  const flattenedPipeline = flattenPipeline(pipeline, exploration);
  return flattenedPipeline.baseModelId;
};

export function flattenPipeline(pipeline: Pipeline, exploration: Exploration): BasePipeline {
  if (isPipelineWithParent(pipeline)) {
    const parentPipeline = getPipelineByIdOrThrow(pipeline.parentId, exploration);

    return flattenPipeline(
      {
        pipelineId: pipeline.pipelineId,
        operations: [...parentPipeline.operations, ...pipeline.operations],
        ...(isPipelineWithParent(parentPipeline)
          ? { parentId: parentPipeline.parentId }
          : { baseModelId: parentPipeline.baseModelId }),
      },
      exploration,
    );
  }

  return pipeline;
}

export const canFlattenChildPipelines = (pipeline: Pipeline, exploration: Exploration) =>
  pipeline.operations.length === 0 ||
  !(
    exploration.view.cells.some(
      (cell) =>
        isCellWithPipeline(cell) &&
        isPipelineChildOf(cell.pipeline, pipeline) &&
        isCombinePipeline(cell.pipeline),
    ) ||
    exploration.view.cells.some(
      (cell) =>
        isCellWithPipeline(cell) &&
        cell.pipeline.operations.some(
          (operation) =>
            operation.operation === 'joinPipeline' && isJoinPipelineChildOf(operation, pipeline),
        ),
    )
  );

/**
 * Flatten nested parents and convert references to nested pipelines
 */
export function dereferencePipeline(
  pipeline: Pipeline,
  exploration: Exploration,
): DereferencedPipeline {
  const flattenedPipeline = flattenPipeline(pipeline, exploration);

  return {
    ...flattenedPipeline,
    operations: dereferenceOperations(flattenedPipeline.operations, exploration),
  };
}

export const dereferenceFunnelOperation = (
  operation: FunnelOperation,
  exploration: Exploration,
): DereferencedFunnelOperation => ({
  ...operation,
  parameters: {
    ...operation.parameters,
    steps: operation.parameters.steps
      .filter((step) => !isEmptyStep(step))
      .map((step) => {
        const pipeline = dereferencePipeline(
          getFlattenedStepPipeline(step, exploration),
          exploration,
        );
        return {
          sortKey: step.sortKey ?? null,
          fields: step.fields ?? [],
          modelId: pipeline.baseModelId,
          operations: pipeline.operations,
        };
      }),
  },
});

export const dereferenceOperation = (
  operation: PipelineOperation,
  exploration: Exploration,
): DereferencedPipelineOperation => {
  switch (operation.operation) {
    case 'addRelatedColumn': {
      if (operation.parameters.pipelineId === undefined) {
        return operation;
      }
      const subPipeline = getPipelineByIdOrThrow(operation.parameters.pipelineId, exploration);
      const { pipelineId, ...parameters } = operation.parameters;

      return {
        ...operation,
        parameters: {
          ...parameters,
          pipeline: dereferencePipeline(subPipeline, exploration),
        },
      };
    }
    case 'relationAggregate': {
      if (operation.parameters.pipelineId !== undefined) {
        const { pipelineId, ...restParameters } = operation.parameters;
        return {
          ...operation,
          parameters: {
            ...restParameters,
            pipeline: dereferencePipeline(
              getPipelineByIdOrThrow(pipelineId, exploration),
              exploration,
            ),
          },
        };
      }

      if ('joinStrategy' in operation.parameters) {
        const { pipelineId, ...restParameters } = operation.parameters;
        return {
          ...operation,
          parameters: {
            ...restParameters,
            pipeline: dereferencePipeline(
              getPipelineByIdOrThrow(pipelineId, exploration),
              exploration,
            ),
          },
        };
      }

      const { pipelineId, ...restParameters } = operation.parameters;
      return {
        ...operation,
        parameters: {
          ...restParameters,
        },
      };
    }
    case 'funnel':
      return dereferenceFunnelOperation(operation, exploration);
    case 'cohort':
      return {
        ...operation,
        parameters: {
          ...operation.parameters,
          pipeline: dereferencePipeline(operation.parameters.pipeline, exploration),
        },
      };
    case 'joinPipeline':
      return {
        ...operation,
        parameters: {
          steps: operation.parameters.steps.map((step) => ({
            ...step,
            pipeline: dereferencePipeline(step.pipeline, exploration),
          })),
        },
      };
    default:
      return operation;
  }
};

export const dereferenceOperations = (
  operations: PipelineOperation[],
  exploration: Exploration,
): DereferencedPipelineOperation[] => {
  return operations.map((operation) => dereferenceOperation(operation, exploration));
};

export const getParentPipelineCell = (exploration: Exploration, pipeline: Pipeline) => {
  if (!isPipelineWithParent(pipeline)) {
    return null;
  }

  return (
    exploration.view.cells.find(
      (cell) => isCellWithPipeline(cell) && isPipelineParentOf(cell.pipeline, pipeline),
    ) ?? null
  );
};

export const getParentPipelineTitle = (exploration: Exploration, pipeline: Pipeline) => {
  if (!isPipelineWithParent(pipeline)) {
    return null;
  }

  const cell = getParentPipelineCell(exploration, pipeline);
  return cell && isCellWithTitle(cell) ? (cell.title ?? null) : null;
};

const isAnyPipelineOperationChildOf = (pipeline: Pipeline, parentPipeline: Pipeline) =>
  pipeline.operations.some((operation) => isOperationPipelineChildOf(operation, parentPipeline));

const isOperationPipelineChildOf = (operation: PipelineOperation, parentPipeline: Pipeline) =>
  isJoinPipelineChildOf(operation, parentPipeline) ||
  isRelationAggregateChildOf(operation, parentPipeline) ||
  isAddRelatedColumnChildOf(operation, parentPipeline) ||
  isFunnelChildOf(operation, parentPipeline) ||
  isCohortChildOf(operation, parentPipeline);

const isJoinPipelineChildOf = (operation: PipelineOperation, pipeline: Pipeline) =>
  operation.operation === 'joinPipeline' &&
  extractCombineSteps(operation).some((step) => isPipelineChildOf(step.pipeline, pipeline));

const isRelationAggregateChildOf = (operation: PipelineOperation, parentPipeline: Pipeline) =>
  operation.operation === 'relationAggregate' &&
  operation.parameters.pipelineId === parentPipeline.pipelineId;

const isAddRelatedColumnChildOf = (operation: PipelineOperation, parentPipeline: Pipeline) =>
  operation.operation === 'addRelatedColumn' &&
  operation.parameters.pipelineId === parentPipeline.pipelineId;

const isFunnelChildOf = (operation: PipelineOperation, parentPipeline: Pipeline) =>
  operation.operation === 'funnel' &&
  operation.parameters.steps.some((step) => isPipelineChildOf(step.pipeline, parentPipeline));

const isCohortChildOf = (operation: PipelineOperation, parentPipeline: Pipeline) =>
  operation.operation === 'cohort' &&
  isPipelineChildOf(operation.parameters.pipeline, parentPipeline);

export const getInstanceChildPipelineCells = (exploration: Exploration, pipeline: Pipeline) =>
  exploration.view.cells.filter(
    (cell) => isCellWithPipeline(cell) && isPipelineChildOf(cell.pipeline, pipeline),
  );

export const getNonInstanceChildPipelineCells = (exploration: Exploration, pipeline: Pipeline) =>
  exploration.view.cells.filter(
    (cell) => isCellWithPipeline(cell) && isAnyPipelineOperationChildOf(cell.pipeline, pipeline),
  );

export const getChildPipelineCells = (exploration: Exploration, pipeline: Pipeline) => [
  ...getInstanceChildPipelineCells(exploration, pipeline),
  ...getNonInstanceChildPipelineCells(exploration, pipeline),
];

export const getBasePipelineCells = (exploration: Exploration): CellWithPipeline[] =>
  exploration.view.cells
    .filter(isCellWithPipeline)
    .filter((cell) => !isPipelineWithParent(cell.pipeline));

export const getNumberOfChildPipelines = (exploration: Exploration, pipeline: Pipeline): number =>
  getChildPipelineCells(exploration, pipeline).length;

// TODO: move to core
export const countOperations = (exploration: Exploration) =>
  exploration.view.cells.reduce((count, cell) => {
    if (isCellWithPipeline(cell)) {
      return count + cell.pipeline.operations.length;
    }
    return count;
  }, 0);

type FieldFallbackSuffixes =
  | {
      relationName: string;
      modelName: string;
    }
  | { relationName: string }
  | { modelName: string };

/**
 * Get the preferred suffix for conflicting related fields when adding them to a pipeline.
 * Take cell title if available, otherwise fall back to relation name or model name.
 * Some pipeline references are in the form { parentId } while others are simply pipelineIds.
 * Accepts both for convenience.
 */
export function getRelatedFieldSuffix(
  pipelineOrId: Pipeline | string | undefined,
  exploration: Exploration,
  fallbackSuffixes: FieldFallbackSuffixes,
): string {
  const fallback =
    'relationName' in fallbackSuffixes ? fallbackSuffixes.relationName : fallbackSuffixes.modelName;
  const pipelineId =
    typeof pipelineOrId === 'string'
      ? pipelineOrId
      : pipelineOrId !== undefined && 'parentId' in pipelineOrId
        ? pipelineOrId.parentId
        : undefined;
  if (pipelineId === undefined) {
    return fallback;
  }
  return getCellByPipelineId(exploration.view.cells, pipelineId)?.title ?? fallback;
}

const appendUniqueSuffix = (name: string, fields: Fields, suffix?: string, i = 0): string => {
  const newName = `${name}${suffix !== undefined ? ` (${suffix})` : ''}${i > 0 ? ` (${i})` : ''}`;
  if (fields.some((f) => f.name === newName)) {
    return appendUniqueSuffix(name, fields, suffix, i + 1);
  }
  return newName;
};

export const getUniqueFieldName = (fields: Fields, name: string, suffix?: string) => {
  if (!fields.some((f) => f.name === name)) {
    return name;
  }
  return appendUniqueSuffix(name, fields, suffix);
};

export const suffixConflictingFields = (
  constantFields: Fields,
  renamableFields: Fields,
  suffix: string,
): Fields => {
  const renamedFields = [...renamableFields];
  renamableFields.forEach((field, i) => {
    renamedFields[i] = {
      ...renamedFields[i],
      name: constantFields.some((f) => f.name === field.name)
        ? appendUniqueSuffix(field.name, constantFields, suffix)
        : field.name,
    };
  });
  return renamedFields;
};

export const ensureFakeBaseModelExists = (models: Model[], pipeline: BasePipeline): Model[] => {
  const firstOperation = first(pipeline.operations);
  if (firstOperation === undefined) {
    return models;
  }
  if (
    ['sql', 'python', 'funnel', 'cohort'].includes(firstOperation.operation) &&
    !models.some((m) => m.modelId === pipeline.baseModelId)
  ) {
    return [
      ...models,
      {
        modelId: pipeline.baseModelId,
        name: `Generated Model ${pipeline.baseModelId}`,
        primaryKey: [],
        properties: [],
        relations: [],
        labels: {},
      },
    ];
  }
  return models;
};

export const getBaseModelName = (pipeline: Pipeline, exploration: Exploration, models: Model[]) => {
  const flattenedPipeline = flattenPipeline(pipeline, exploration);
  if (flattenedPipeline.operations.at(0)?.operation === 'sql') {
    return 'SQL';
  }
  const modelsWithFakeBase = ensureFakeBaseModelExists(models, flattenedPipeline);
  const baseModel = modelsWithFakeBase.find((m) => m.modelId === flattenedPipeline.baseModelId);
  return baseModel ? baseModel.name : `Unknown Model ${flattenedPipeline.baseModelId}`;
};

export const getPipelineWithRollback = (pipeline: Pipeline, rollbackIndex?: number): Pipeline => {
  if (rollbackIndex !== undefined) {
    return {
      ...pipeline,
      operations: pipeline.operations.slice(0, rollbackIndex),
    };
  }
  return pipeline;
};
